// DTO shapes mirrored from the API contract (openapi/). Kept minimal for the first slice.

export interface Money {
  minor: string;
  currency: string;
  display: string;
}

export type InvoiceStatus = 'draft' | 'issued' | 'frozen' | 'voided';

export interface Invoice {
  id: string;
  number: string | null;
  kind: 'invoice' | 'credit_note';
  status: InvoiceStatus;
  currency: string;
  issue_date: string | null;
  due_date: string | null;
  total: Money;
  outstanding?: Money | null;
  paid?: Money | null;
  billing_party_id?: string;
  client_name?: string | null;
  accounting_state: string;
}

export interface Payment {
  id: string;
  invoice_id: string;
  amount: Money;
  paid_at: string;
  method: string | null;
  reference: string | null;
  source: string;
  reversed: boolean;
  created_at: string;
}

export interface Client {
  id: string;
  name: string;
  billing_party_id: string | null;
  status: string;
  currency?: string;
}

export type TechnicianStatus = 'active' | 'inactive';
export type Employment = 'employee' | 'contractor';

export interface Technician {
  id: string;
  name: string;
  email: string | null;
  status: TechnicianStatus;
  employment: Employment;
  cost_rate_minor: string;
  default_bill_rate_minor: string;
  currency: string;
  weekly_capacity_minutes: number;
}

export interface TechnicianCostRate {
  id: string;
  technician_id: string;
  effective_from: string;
  effective_to: string | null;
  cost_rate_minor: string;
  currency: string;
  note: string | null;
}

export type ContractStatus = 'pending' | 'active' | 'expired' | 'cancelled';
export type ContractCycle =
  | 'weekly'
  | 'monthly'
  | 'quarterly'
  | 'biannual'
  | 'annual'
  | 'oneoff'
  | 'custom';

export interface Contract {
  id: string;
  billing_party_id: string;
  client_id: string | null;
  ref: string | null;
  status: ContractStatus;
  cycle: ContractCycle;
  period_charge_minor: string;
  currency: string;
  start_date: string | null;
  end_date: string | null;
  next_invoice_date: string | null;
  client_name?: string | null;
}

export type TimesheetStatus = 'open' | 'submitted' | 'approved' | 'locked';

export interface Timesheet {
  id: string;
  technician_id: string;
  period_start: string;
  period_end: string;
  status: TimesheetStatus;
  locked_at: string | null;
  locked_by: string | null;
  total_minutes: number;
}

export interface InvoiceLine {
  id: string;
  invoice_id: string;
  line_no: number;
  description: string;
  quantity: string;
  unit_price_minor: string;
  net_minor: string;
  tax_minor: string;
  contract_id: string | null;
  time_entry_id: string | null;
}

export interface PrepayAccount {
  id: string;
  contract_id: string;
  billing_party_id: string;
  unit: 'hours' | 'money';
  currency: string;
  status: string;
  expiry_months: number | null;
  client_name?: string | null;
}

export interface PrepayBalance {
  prepay_account_id?: string;
  remaining_qty: string;
  purchased_qty?: string;
  consumed_qty?: string;
  unit?: 'hours' | 'money';
  currency?: string;
}

export type ChargeType = 'tm' | 'prepay' | 'contract_included' | 'non_billable';

/** Human labels for charge types — raw enum values never reach the screen. */
export const CHARGE_TYPE_LABELS: Record<ChargeType, string> = {
  tm: 'Time & materials',
  prepay: 'Prepaid balance',
  contract_included: 'Included in contract',
  non_billable: 'Non-billable',
};

/** A billable T&M entry awaiting invoicing (the billing run's worklist). */
export interface UnbilledEntry {
  id: string;
  occurred_on: string | null;
  minutes: number;
  billable_amount_minor: string;
  bill_rate_minor: string;
  note: string | null;
  client_id: string | null;
  contract_id: string | null;
  technician_name: string;
  currency: string;
  client_name: string | null;
  billing_party_id: string | null;
  timesheet_status: TimesheetStatus;
}

export interface TimeEntry {
  id: string;
  technician_id: string;
  ticket_id: string | null;
  client_id: string | null;
  contract_id: string | null;
  timesheet_id: string | null;
  occurred_on: string | null;
  minutes: number;
  billable: boolean;
  charge_type: ChargeType;
  cost_rate_minor: string;
  bill_rate_minor: string;
  cost_minor: string;
  billable_amount_minor: string;
  currency: string;
  note: string | null;
  locked_at: string | null;
}

// ---- Ledger (system of record) ----

export type LedgerAccountType = string;

export interface LedgerAccount {
  id: string;
  account_type: LedgerAccountType;
  billing_party_id: string | null;
  contract_id: string | null;
  prepay_account_id: string | null;
  currency: string;
  running_amount_minor: string;
  running_quantity: string;
  last_seq: string;
  party_name?: string | null;
}

export interface LedgerEntry {
  id: string;
  seq: string;
  account_seq: string;
  entry_type: string;
  amount_minor: string;
  quantity: string | null;
  currency: string;
  occurred_at: string;
  invoice_id: string | null;
  invoice_number?: string | null;
  contract_id: string | null;
  prepay_account_id: string | null;
  reverses_entry_id: string | null;
  memo: string | null;
  created_by: string | null;
  created_at: string;
}

export interface LedgerBalance {
  balance_minor: string;
  quantity: string;
}

export interface ReconException {
  id: string;
  integration: string;
  entity_type: string;
  external_id: string | null;
  local_id: string | null;
  kind: string;
  detail: unknown;
  status: string;
  detected_at: string;
  resolved_at: string | null;
}

// ---- Stats (server-side dashboard aggregates) ----

export interface Stats {
  invoices: { by_status: Record<string, number>; credit_notes: number };
  ar: { currency: string; total_minor: string; parties: number }[];
  overdue: { currency: string; count: number; total_minor: string }[];
  prepay: { accounts: number; hours_remaining: string; overdrawn: number };
  recon_open: number;
  unbilled: { currency: string; minutes: number; amount_minor: string }[];
  counts: { clients: number; technicians: number; time_entries: number };
}

// ---- Tenant settings ----

export interface TenantSettings {
  timesheet_cycle: 'weekly' | 'monthly';
  week_starts_on: number; // 0 = Sunday … 6 = Saturday
  timesheet_approval: 'none' | 'required';
  require_locked_time_to_invoice: boolean;
}

// ---- Team ----

export interface Member {
  user_id: string;
  user_email: string;
  full_name: string | null;
  role: string;
  status: string;
  created_at: string;
}

export interface TenantInvite {
  id: string;
  email: string;
  role: string;
  invited_by: string | null;
  expires_at: string;
  accepted_at: string | null;
  created_at: string;
  token?: string;
}

// ---- Report view rows (raw metric columns; *_minor / *_qty arrive as strings) ----

export interface TicketProfitabilityRow {
  ticket_id: string;
  ref: string | null;
  client_name?: string | null;
  client_id: string | null;
  assigned_tech_id: string | null;
  minutes: string;
  cost_minor: string;
  billable_amount_minor: string;
  invoiced_minor: string;
  margin_minor: string;
}

export interface ClientProfitabilityRow {
  client_id: string;
  name: string;
  cost_minor: string;
  tm_billable_minor: string;
  invoiced_minor: string;
  margin_minor: string;
}

export interface PrepayBalanceRow {
  prepay_account_id: string;
  contract_id: string;
  billing_party_id: string;
  client_name?: string | null;
  contract_ref?: string | null;
  unit: 'hours' | 'money';
  currency: string;
  purchased_qty: string;
  consumed_qty: string;
  remaining_qty: string;
  collected_minor: string;
  over_drawn: boolean;
  last_topup_at: string | null;
}

export interface ArBalanceRow {
  billing_party_id: string;
  name: string;
  currency: string;
  ar_balance_minor: string;
}
